import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import classNames from 'classnames';

import {
    validateAcceptTerms,
    validateAmount,
    validateFreight,
} from './enterAmountValidator.js';
import { trackEventObject } from '../../../services/trackingService';
import { createAgreement } from '../agreement/agreementActions';
import formatOreToKroner from '../../../lib/formatOreToKroner';
import agreementPropTypes from '../../agreementPropTypes';
import partnerPropTypes from '../../partnerPropTypes';
import { minutesToInWords } from '../../getDeadline.js';
import Page from '../../page.jsx';

const oreFromKronerString = e => Number(e.target.value.replace(',', '.')) * 100;

class EnterAmount extends PureComponent {
    constructor() {
        super();
        this.state = {
            freightOre: null,
            amountOre: null,
            termsAccepted: false,
            submitted: false,
            buyerPaysFreight: true,
        };
        this.handleAmountChange = this.handleAmountChange.bind(this);
        this.handleFreightChange = this.handleFreightChange.bind(this);
        this.updateBuyerFreight = this.updateBuyerFreight.bind(this);
        this.updateSellerFreight = this.updateSellerFreight.bind(this);
        this.handleClickTermsAccepted = this.handleClickTermsAccepted.bind(
            this
        );
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    componentDidMount() {
        trackEventObject({
            analyticsName: 'trade.make-agreement.buyer-opens-form',
            context: {
                finnkode: this.props.itemId,
            },
        });
    }

    handleAmountChange(e) {
        this.setState({
            amountOre: oreFromKronerString(e),
        });
    }

    handleFreightChange(e) {
        this.setState({
            freightOre: oreFromKronerString(e),
        });
    }

    updateBuyerFreight() {
        this.setState({
            buyerPaysFreight: true,
        });
    }

    updateSellerFreight() {
        this.setState({
            buyerPaysFreight: false,
        });
    }

    handleClickTermsAccepted() {
        this.setState({
            termsAccepted: !this.state.termsAccepted,
        });
    }

    getFreightOre() {
        return this.state.buyerPaysFreight ? this.state.freightOre : 0;
    }

    validate() {
        const { maxAmount } = this.props;
        const {
            amountOre,
            buyerPaysFreight,
            freightOre,
            termsAccepted,
        } = this.state;
        const acceptTermsValid = validateAcceptTerms({ termsAccepted });
        const amountValid = validateAmount({
            amountOre,
            freightOre,
            maxAmountOre: maxAmount,
        });
        const freightValid = validateFreight({ buyerPaysFreight, freightOre });

        return { acceptTermsValid, amountValid, freightValid };
    }

    isValid() {
        const validity = this.validate();
        return Object.values(validity).every(v => !v.invalid);
    }

    handleSubmit() {
        const { itemType, itemId, partner, handleCreateAgreement } = this.props;
        this.setState({
            submitted: true,
        });

        const amountOre = this.state.amountOre;
        const freightOre = this.getFreightOre();

        if (this.isValid()) {
            handleCreateAgreement({
                itemType,
                itemId,
                partnerId: partner.id,
                amountOre,
                freightOre,
            });
        }
    }

    showInvalid(fieldValidity) {
        const { submitted } = this.state;

        return submitted && fieldValidity.invalid;
    }

    render() {
        const {
            acceptanceTimeLimitInMinutes,
            agreement,
            partner,
            feePercentage,
        } = this.props;
        const { amountOre, buyerPaysFreight, termsAccepted } = this.state;

        const freightOre = this.getFreightOre();

        const feeOre =
            ((amountOre || 0) + (freightOre || 0)) * (feePercentage / 100);
        const totalOre = (amountOre || 0) + feeOre + (freightOre || 0);
        const amountKroner = Math.floor((amountOre || 0) / 100);
        const freightKroner =
            typeof freightOre === 'number'
                ? Math.floor(freightOre / 100)
                : freightOre;
        const { acceptTermsValid, amountValid, freightValid } = this.validate();
        const freightInputClasses = classNames(
            'input inlineblockify fright size1of3',
            {
                invisible: !buyerPaysFreight,
                invalid: this.showInvalid(freightValid),
            }
        );

        return (
            <Page heading="Start Trygg betaling">
                <div>
                    <p className="largetext r-mlm">
                        Hva har du og {partner.name} blitt enige om?
                    </p>

                    <div
                        className={classNames('line r-phm r-mtl', {
                            invalid: this.showInvalid(amountValid),
                        })}
                    >
                        <label className="unit size2of3 pvs" htmlFor="amount">
                            Avtalt pris for varen
                        </label>
                        <div className="input unit size1of3">
                            <input
                                id="amount"
                                className="rightify"
                                type="number"
                                value={amountKroner || ''}
                                onChange={this.handleAmountChange}
                            />
                            {this.showInvalid(amountValid) && (
                                <span className="help-inline">
                                    {amountValid.message}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="r-phm ptl">Hvem betaler for frakt?</div>

                    <div className="line r-phm r-mvl">
                        <div
                            className="inlineblockify input pll size2of3"
                            role="radiogroup"
                        >
                            <div
                                className={classNames('fancyinput pvs r-pbl', {
                                    invalid: this.showInvalid(freightValid),
                                })}
                                role="radio"
                            >
                                <input
                                    id="buyer-pays-freight"
                                    type="radio"
                                    checked={buyerPaysFreight}
                                    onChange={this.updateBuyerFreight}
                                    name="freight_payer"
                                />
                                <label htmlFor="buyer-pays-freight">
                                    {' '}
                                    Du betaler frakt
                                </label>
                            </div>
                            <div className="fancyinput" role="radio">
                                <input
                                    id="seller-pays-freight"
                                    type="radio"
                                    checked={!buyerPaysFreight}
                                    onChange={this.updateSellerFreight}
                                    name="freight_payer"
                                />
                                <label htmlFor="seller-pays-freight">
                                    {' '}
                                    {partner.name} betaler frakt
                                </label>
                            </div>
                        </div>

                        <div className={freightInputClasses}>
                            <input
                                type="number"
                                onChange={this.handleFreightChange}
                                value={
                                    typeof freightKroner === 'number'
                                        ? freightKroner
                                        : ''
                                }
                                id="freight"
                                className="rightify"
                            />
                            {this.showInvalid(freightValid) && (
                                <span className="help-inline">
                                    {freightValid.message}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="line r-phm r-mtm">
                        <span className="unit size2of3"> + Gebyr til FINN</span>
                        <span className="unit size1of3 rightify">
                            {!!feeOre && formatOreToKroner(feeOre)}
                        </span>
                    </div>

                    <hr />

                    <div className="line r-phm r-mtl secondary-blue">
                        <span className="unit strong size2of3">Totalt</span>
                        <span className="unit strong size1of3 rightify">
                            {formatOreToKroner(totalOre)}
                        </span>
                    </div>

                    <p className="r-mlm r-mtl r-ptl">
                        {partner.name} må bekrefte at dere er enige om denne
                        avtalen innen{' '}
                        <strong>
                            {minutesToInWords(acceptanceTimeLimitInMinutes)}
                        </strong>.
                    </p>

                    <div
                        className={classNames('formelement r-mvl r-mlm pbl', {
                            invalid: this.showInvalid(acceptTermsValid),
                        })}
                    >
                        <div className="input">
                            <div className="fancyinput">
                                <input
                                    id="accept-terms"
                                    type="checkbox"
                                    onClick={this.handleClickTermsAccepted}
                                    value={termsAccepted}
                                />
                                <label htmlFor="accept-terms">
                                    Jeg forstår at avtalen er bindende.
                                </label>
                                {this.showInvalid(acceptTermsValid) && (
                                    <span className="help-inline">
                                        {acceptTermsValid.message}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="r-phm">
                        <button
                            className={classNames('fullwidth primary', {
                                inprogress: agreement.loading,
                            })}
                            disabled={agreement.loading}
                            onClick={this.handleSubmit}
                        >
                            Send avtale
                        </button>
                        <div className="r-mtl centerify">
                            <Link to="/samtale">Avbryt</Link>
                        </div>
                    </div>
                </div>
            </Page>
        );
    }
}

EnterAmount.propTypes = {
    acceptanceTimeLimitInMinutes: PropTypes.number.isRequired,
    agreement: agreementPropTypes.isRequired,
    itemId: PropTypes.string.isRequired,
    itemType: PropTypes.string.isRequired,
    maxAmount: PropTypes.number.isRequired,
    partner: partnerPropTypes.isRequired,
    handleCreateAgreement: PropTypes.func.isRequired,
    feePercentage: PropTypes.number.isRequired,
};

function mapStateToProps(state) {
    return {
        acceptanceTimeLimitInMinutes:
            state.safeTrade.acceptanceTimeLimitInMinutes,
        agreement: state.agreement,
        itemId: state.safeTrade.itemId,
        itemType: state.safeTrade.itemType,
        maxAmount: state.safeTrade.maxAmount,
        partner: state.partner,
        feePercentage: state.safeTrade.feePercentage,
    };
}

export default connect(mapStateToProps, {
    handleCreateAgreement: createAgreement,
})(EnterAmount);



// WEBPACK FOOTER //
// ./safeTrade/buyer/agreement/enterAmount.jsx